import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
  User as FirebaseUser,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { User } from '../types';
import { ShippixService } from '../services/shippixService';
import { updateOnlineStatus } from '../utils/onlineStatus';

interface AuthContextType {
  currentUser: (FirebaseUser & Partial<User>) | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<any>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  supportLogin: (targetUser: User) => Promise<void>;
  endSupportLogin: () => Promise<void>;
  originalUser: (FirebaseUser & Partial<User>) | null;
  isInSupportMode: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<(FirebaseUser & Partial<User>) | null>(null);
  const [originalUser, setOriginalUser] = useState<(FirebaseUser & Partial<User>) | null>(null);
  const [loading, setLoading] = useState(true);
  const [unsubscribeUser, setUnsubscribeUser] = useState<(() => void) | undefined>();
  const [isInSupportMode, setIsInSupportMode] = useState(false);

  // Set persistence to LOCAL on mount
  useEffect(() => {
    setPersistence(auth, browserLocalPersistence).catch(console.error);
  }, []);

  // Gerenciar status online/offline
  useEffect(() => {
    if (!currentUser?.uid) return;

    let unloading = false;

    // Atualizar status para online quando o usuário está ativo
    updateOnlineStatus(currentUser.uid, true);

    // Configurar listeners para detectar quando o usuário fica offline
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        updateOnlineStatus(currentUser.uid, false);
      } else {
        updateOnlineStatus(currentUser.uid, true);
      }
    };

    const handleOffline = () => {
      updateOnlineStatus(currentUser.uid, false);
    };

    const handleOnline = () => {
      updateOnlineStatus(currentUser.uid, true);
    };

    // Adicionar event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    // Configurar cleanup quando o usuário fecha a página
    const beforeUnloadHandler = () => {
      if (!unloading) {
        unloading = true;
        updateOnlineStatus(currentUser.uid, false);
      }
    };
    window.addEventListener('beforeunload', beforeUnloadHandler);

    return () => {
      // Cleanup
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      updateOnlineStatus(currentUser.uid, false);
    };
  }, [currentUser?.uid]);

  useEffect(() => {
    let unsubscribeAuth: () => void;

    unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      try {
        if (user) {
          // Get initial user data
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          
          if (userDoc.exists()) {
            const userData = userDoc.data() as Partial<User>;
            
            // Check if user is a marketplace customer
            if (userData.role === 'cliente') {
              await signOut(auth);
              setCurrentUser(null);
              window.location.href = 'https://www.emtuacasa.pt';
              return;
            }

            const fullUser = {
              ...user,
              ...userData
            };
            setCurrentUser(fullUser);

            // Set up user data listener with error handling
            try {
              const unsub = onSnapshot(
                doc(db, 'users', user.uid),
                {
                  next: (doc) => {
                    if (doc.exists()) {
                      const updatedUserData = doc.data() as Partial<User>;
                      setCurrentUser(prevUser => {
                        if (!prevUser) return null;
                        return {
                          ...(prevUser as FirebaseUser),
                          ...updatedUserData
                        };
                      });
                    }
                  },
                  error: (error) => {
                    console.error('User document listener error:', error);
                  }
                }
              );
              setUnsubscribeUser(() => unsub);
            } catch (listenerError) {
              console.error('Error setting up user document listener:', listenerError);
            }
          } else {
            const defaultUser = {
              ...user,
              role: 'gerente',
              isOnline: true
            };
            setCurrentUser(defaultUser);
          }
        } else {
          setCurrentUser(null);
        }
      } catch (error) {
        console.error('Error in auth state change:', error);
        setCurrentUser(null);
      } finally {
        setLoading(false);
      }
    });

    return () => {
      if (unsubscribeAuth) unsubscribeAuth();
      if (unsubscribeUser) unsubscribeUser();
    };
  }, []);

  // Adicionar validação de role permitida após login
  useEffect(() => {
    if (currentUser && currentUser.role === 'customer') {
      window.location.href = 'https://www.emtuacasa.pt';
    }
  }, [currentUser]);

  const login = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const register = async (email: string, password: string) => {
    return await createUserWithEmailAndPassword(auth, email, password);
  };

  const logout = async () => {
    if (currentUser?.uid) {
      await updateOnlineStatus(currentUser.uid, false);
    }
    await signOut(auth);
    setCurrentUser(null);
  };

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  const supportLogin = async (targetUser: User) => {
    if (!currentUser?.role || currentUser.role !== 'admin') {
      throw new Error('Apenas administradores podem usar o modo suporte');
    }

    // Guardar o usuário original
    setOriginalUser(currentUser);
    setIsInSupportMode(true);

    // Buscar os dados completos do usuário alvo
    const userDoc = await getDoc(doc(db, 'users', targetUser.id));
    if (!userDoc.exists()) {
      throw new Error('Usuário não encontrado');
    }

    // Atualizar o currentUser com os dados do usuário alvo
    const userData = userDoc.data() as User;
    setCurrentUser({ ...currentUser, ...userData });
  };

  const endSupportLogin = async () => {
    if (!originalUser) return;

    // Restaurar o usuário original
    setCurrentUser(originalUser);
    setOriginalUser(null);
    setIsInSupportMode(false);
  };

  const value = {
    currentUser,
    loading,
    login,
    register,
    logout,
    resetPassword,
    supportLogin,
    endSupportLogin,
    originalUser,
    isInSupportMode
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}